/**
 * CampaignsWidget - Виджет кампаний
 * ==================================
 * 
 * Полностью переписан с использованием новой архитектуры
 */

import React from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useFilters } from '../../hooks/useFilters';
import { MetricsService } from '../../services/metricsService';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { companies as companiesSpecs } from '@ggstat/chart-specs';

// Пороги статуса по ROI — общие с MCP (shared/chart-specs/specs/companies.json)
const CAMPAIGN_STATUS = companiesSpecs.campaignStatus;

/**
 * Компонент тренда
 */
function TrendIndicator({ value, previousValue }) {
  if (!previousValue || previousValue === 0) {
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  }
  
  const change = ((value - previousValue) / previousValue) * 100;
  
  if (Math.abs(change) < 1) {
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  }
  
  const isPositive = change > 0;
  const Icon = isPositive ? TrendingUp : TrendingDown;
  const color = isPositive ? 'text-green-600' : 'text-red-600';
  
  return (
    <div className={`flex items-center gap-1 ${color}`}>
      <Icon className="h-4 w-4" />
      <span className="text-xs font-medium">
        {Math.abs(change).toFixed(1)}%
      </span>
    </div>
  );
}

/**
 * Компонент строки кампании
 */
function CampaignRow({ campaign }) {
  // Считаем total revenue
  const inapps = campaign.revenue?.inapps || 0;
  const admon = campaign.revenue?.admon || 0;
  const totalRevenue = inapps + admon;
  
  const roi = MetricsService.calculateROI(totalRevenue, campaign.cost);
  const cpi = MetricsService.calculateCPI(campaign.cost, campaign.installs);
  
  // Определяем статус кампании по ROI
  const getStatus = () =>
    CAMPAIGN_STATUS.thresholds.find((t) => roi > t.minRoi) || CAMPAIGN_STATUS.fallback;
  
  const status = getStatus();
  
  return (
    <TableRow>
      <TableCell className="font-medium">
        <div>
          <p className="font-medium">{campaign.campaign}</p>
          <p className="text-xs text-muted-foreground">{campaign.mediaSource}</p>
        </div>
      </TableCell>
      
      <TableCell>
        <Badge variant={status.variant}>{status.label}</Badge>
      </TableCell>
      
      <TableCell className="text-right">
        <div>
          <p className="font-medium">
            {MetricsService.formatNumber(campaign.installs)}
          </p>
          <p className="text-xs text-muted-foreground">
            CPI: {MetricsService.formatCurrency(cpi)}
          </p>
        </div>
      </TableCell>
      
      <TableCell className="text-right">
        <div>
          <p className="font-medium">
            {MetricsService.formatCurrency(campaign.cost)}
          </p>
          <TrendIndicator 
            value={campaign.cost} 
            previousValue={campaign.previousCost}
          />
        </div>
      </TableCell>
      
      {/* Inapps Revenue */}
      <TableCell className="text-right">
        <p className="font-medium">
          {MetricsService.formatCurrency(inapps)}
        </p>
        <p className="text-xs text-muted-foreground">Purchases</p>
      </TableCell>
      
      {/* AdMon Revenue */}
      <TableCell className="text-right">
        <p className="font-medium">
          {MetricsService.formatCurrency(admon)}
        </p>
        <p className="text-xs text-muted-foreground">Ads</p>
      </TableCell>
      
      {/* Total Revenue */}
      <TableCell className="text-right">
        <div>
          <p className="font-medium">
            {MetricsService.formatCurrency(totalRevenue)}
          </p>
          <p className={`text-xs font-medium ${totalRevenue >= campaign.cost ? 'text-green-600' : 'text-red-600'}`}>
            ROAS: {campaign.cost > 0 ? ((totalRevenue / campaign.cost) * 100).toFixed(1) : '0.0'}%
          </p>
        </div>
      </TableCell>
      
      <TableCell>
        <Progress 
          value={Math.min(Math.max(roi + 50, 0), 100)} 
          className="h-2"
        />
      </TableCell>
    </TableRow>
  );
}

/**
 * Компонент сводки
 */
function CampaignsSummary({ campaigns, serverTotals }) {
  // Используем totals с сервера, если они есть, иначе считаем сами
  const totals = serverTotals || campaigns.reduce((acc, campaign) => {
    const inapps = campaign.revenue?.inapps || 0;
    const admon = campaign.revenue?.admon || 0;
    return {
      installs: acc.installs + campaign.installs,
      cost: acc.cost + campaign.cost,
      inapps: acc.inapps + inapps,
      admon: acc.admon + admon,
      revenue: acc.revenue + inapps + admon
    };
  }, { installs: 0, cost: 0, inapps: 0, admon: 0, revenue: 0 });
  
  // Если сервер не прислал разбивку по inapps/admon, считаем сами
  if (!totals.inapps && !totals.admon) {
    totals.inapps = campaigns.reduce((sum, c) => sum + (c.revenue?.inapps || 0), 0);
    totals.admon = campaigns.reduce((sum, c) => sum + (c.revenue?.admon || 0), 0);
    totals.revenue = totals.inapps + totals.admon;
  }
  
  const avgCPI = MetricsService.calculateCPI(totals.cost, totals.installs);
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 bg-muted/50 rounded-lg">
      <div>
        <p className="text-xs text-muted-foreground">Total Installs</p>
        <p className="text-lg font-bold">{MetricsService.formatNumber(totals.installs)}</p>
        <p className="text-xs text-muted-foreground">Avg CPI: {MetricsService.formatCurrency(avgCPI)}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Total Cost</p>
        <p className="text-lg font-bold">{MetricsService.formatCurrency(totals.cost)}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Inapps Revenue</p>
        <p className="text-lg font-bold">{MetricsService.formatCurrency(totals.inapps)}</p>
        <p className="text-xs text-muted-foreground">Purchases</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">AdMon Revenue</p>
        <p className="text-lg font-bold">{MetricsService.formatCurrency(totals.admon)}</p>
        <p className="text-xs text-muted-foreground">Ads</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Total Revenue</p>
        <p className="text-lg font-bold">{MetricsService.formatCurrency(totals.revenue)}</p>
        <p className={`text-xs font-medium ${totals.revenue >= totals.cost ? 'text-green-600' : 'text-red-600'}`}>
          ROAS: {totals.cost > 0 ? ((totals.revenue / totals.cost) * 100).toFixed(1) : '0.0'}%
        </p>
      </div>
    </div>
  );
}

/**
 * Основной компонент виджета
 */
export function CampaignsWidget() {
  const { platform } = useFilters();
  
  // Читаем состояние чекбокса из localStorage напрямую
  const [shouldApplyFees, setShouldApplyFees] = React.useState(() => {
    const stored = localStorage.getItem('applyPlatformFees');
    return stored === 'true';
  });
  
  // Слушаем изменения в localStorage (когда чекбокс меняется в Sidebar)
  React.useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('applyPlatformFees');
      setShouldApplyFees(stored === 'true');
    };
    
    window.addEventListener('storage', handleStorageChange);
    // Также слушаем кастомное событие для изменений в том же окне
    window.addEventListener('platformFeesChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('platformFeesChanged', handleStorageChange);
    };
  }, []);
  
  // Загружаем данные БЕЗ применения коэффициентов
  const widgetData = useWidgetData(async (filters, options) => {
    const response = await MetricsService.getCampaigns(filters, options);
    console.log('CampaignsWidget: Raw response from API:', response);
    
    // API возвращает объект { campaigns: [...], totals: {...} }
    const campaigns = response?.campaigns || [];
    const totals = response?.totals || null;
    console.log('CampaignsWidget: Extracted campaigns:', campaigns);
    console.log('CampaignsWidget: Extracted totals from server:', totals);
    
    // Сортируем по затратам
    if (Array.isArray(campaigns)) {
      const sorted = campaigns.sort((a, b) => b.cost - a.cost);
      console.log('CampaignsWidget: Sorted campaigns:', sorted);
      // Возвращаем объект с campaigns и totals с сервера
      return { campaigns: sorted, totals };
    }
    console.log('CampaignsWidget: Not an array, returning empty');
    return { campaigns: [], totals: null };
  });
  
  // Используем хелпер для BaseWidget
  const widgetProps = useBaseWidget(widgetData);
  let { data } = widgetProps;
  
  // Извлекаем campaigns и totals из data
  let campaigns = data?.campaigns || data;
  let totals = data?.totals || null;
  
  // Применяем коэффициенты платформ ПОСЛЕ загрузки, если чекбокс включен
  if (shouldApplyFees && campaigns && Array.isArray(campaigns)) {
    console.log('CampaignsWidget: Applying platform fees');
    console.log('CampaignsWidget: shouldApplyFees =', shouldApplyFees);
    console.log('CampaignsWidget: platform =', platform);
    console.log('CampaignsWidget: campaigns[0] BEFORE:', campaigns[0]);
    
    campaigns = MetricsService.applyPlatformFees(campaigns, true, platform);
    
    console.log('CampaignsWidget: campaigns[0] AFTER:', campaigns[0]);
    console.log('CampaignsWidget: Applied platform fees to campaigns');
  }
  
  console.log('CampaignsWidget: Final campaigns data:', campaigns);
  console.log('CampaignsWidget: shouldApplyFees =', shouldApplyFees, 'platform =', platform);
  
  return (
    <BaseWidget
      {...widgetProps}
      title="Campaigns Performance"
      subtitle={totals ? `Total Installs: ${MetricsService.formatNumber(totals.installs)}` : (campaigns ? `${campaigns.length} active campaigns` : null)}
      emptyMessage="No campaigns data available"
      skeletonRows={5}
    >
      <div className="space-y-4">
        {/* Сводка */}
        {campaigns && campaigns.length > 0 && (
          <CampaignsSummary campaigns={campaigns} serverTotals={totals} />
        )}
        
        {/* Таблица кампаний */}
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Installs</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">Inapps</TableHead>
                <TableHead className="text-right">AdMon</TableHead>
                <TableHead className="text-right">Total Revenue</TableHead>
                <TableHead>Performance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns?.map((campaign, index) => (
                <CampaignRow key={index} campaign={campaign} />
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </BaseWidget>
  );
}

export default CampaignsWidget;
